import { HugeiconsIcon } from '@hugeicons/react';
import {
  Settings02Icon,
  Target02Icon,
  Shield01Icon,
  ShieldEnergyIcon,
  Alert02Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  File01Icon,
  FolderLibraryIcon,
  UserCheck01Icon,
  Analytics01Icon,
} from '@hugeicons/core-free-icons';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * PoliticaIAInfografia
 *
 * Réplica interactiva (HTML + iconos + diagramas) de la infografía oficial
 * "Uso ético, responsable y seguro de IA en UC CHRISTUS". Reemplaza la imagen
 * estática por contenido semántico, accesible y responsivo, respetando la
 * identidad de marca (primary #671E75, secondary #307FE2).
 */

const RIESGOS = [
  {
    id: 'bajo',
    titulo: 'Riesgo bajo',
    icon: Shield01Icon,
    accent: 'text-secondary',
    ring: 'border-secondary/25',
    dot: 'bg-secondary',
    descripcion: 'Uso instrumental y de productividad, sin carácter decisor.',
    detalle: 'No requiere evaluación adicional si la herramienta y los datos están autorizados.',
    tag: 'Permitido',
    tagClass: 'bg-secondary/10 text-foreground',
  },
  {
    id: 'medio',
    titulo: 'Riesgo medio',
    icon: Alert02Icon,
    accent: 'text-primary',
    ring: 'border-primary/25',
    dot: 'bg-primary',
    descripcion: 'Uso que puede influir directa o indirectamente en las personas.',
    detalle: 'Requiere Evaluación de Impacto y Riesgo, autorización previa y supervisión humana.',
    tag: 'Permitido con control',
    tagClass: 'bg-primary/10 text-primary',
    /* text-primary (#671E75) sobre fondo tenue: contraste AA para texto pequeño */
  },
  {
    id: 'inaceptable',
    titulo: 'Riesgo inaceptable',
    icon: CancelCircleIcon,
    accent: 'text-destructive',
    ring: 'border-destructive/30',
    dot: 'bg-destructive',
    descripcion:
      'Uso que puede afectar derechos, seguridad del paciente, prestaciones, decisiones clínicas, continuidad operacional o reputación institucional.',
    detalle: 'PROHIBIDO en la institución, aun cuando la herramienta esté autorizada.',
    tag: 'Prohibido',
    tagClass: 'bg-destructive/10 text-destructive',
  },
];

const RUTA = [
  { n: 1, label: 'Solicitud', icon: File01Icon },
  { n: 2, label: 'Evaluación de Impacto de Riesgo', icon: ShieldEnergyIcon },
  { n: 3, label: 'Registro y clasificación', icon: FolderLibraryIcon },
  { n: 4, label: 'Autorización', icon: Shield01Icon },
  { n: 5, label: 'Supervisión humana', icon: UserCheck01Icon },
  { n: 6, label: 'Monitoreo y mejora', icon: Analytics01Icon },
];

const RESGUARDOS = [
  'Dignidad de las personas',
  'Supervisión humana',
  'Seguridad del paciente',
  'Protección de la información',
];

export function PoliticaIAInfografia() {
  const shouldReduce = useReducedMotion();
  const Anim = shouldReduce ? 'div' : motion.div;
  const enter = (delay = 0) =>
    shouldReduce
      ? {}
      : {
          // animate (no whileInView) garantiza que el contenido SIEMPRE quede
          // visible al montar; el contenido nunca depende del trigger de scroll.
          initial: { opacity: 0, y: 14 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.4, delay },
        };

  return (
    <div className="overflow-hidden rounded-3xl border border-border bg-card">
      {/* Header */}
      <div className="bg-primary px-6 py-6 text-center sm:px-10 sm:py-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-white/70">
          Política de Inteligencia Artificial · Aprobada el 14 de julio de 2026
        </p>
        <h2 className="mt-2 font-display text-2xl font-extrabold leading-tight text-white sm:text-3xl">
          Uso ético, responsable y seguro de IA en UC CHRISTUS
        </h2>
      </div>

      <div className="space-y-12 px-5 py-10 sm:px-10">
        {/* Dos niveles de control */}
        <section aria-labelledby="niveles-control">
          <SectionTitle id="niveles-control">La política opera en dos niveles de control</SectionTitle>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <Anim {...enter(0)}>
              <ControlCard
                numero="1"
                titulo="La herramienta"
                icon={Settings02Icon}
                accent="secondary"
                descripcion="UC CHRISTUS evalúa cada herramienta desde una perspectiva técnica y de seguridad antes de autorizar su uso institucional."
                nota="Una herramienta autorizada no puede utilizarse para cualquier actividad."
              />
            </Anim>
            <Anim {...enter(0.08)}>
              <ControlCard
                numero="2"
                titulo="El uso"
                icon={Target02Icon}
                accent="primary"
                descripcion="Se evalúa para qué se utilizará la herramienta, qué datos procesará y qué impacto puede generar."
                nota="Lo que se clasifica es el uso, no solo la herramienta."
              />
            </Anim>
          </div>
        </section>

        {/* Tres niveles de riesgo */}
        <section aria-labelledby="niveles-riesgo">
          <SectionTitle id="niveles-riesgo">Tres niveles de riesgo</SectionTitle>
          <div className="mt-6 grid gap-5 md:grid-cols-3">
            {RIESGOS.map((r, i) => (
              <Anim key={r.id} {...enter(i * 0.08)} className="h-full">
                <div className={`flex h-full flex-col rounded-2xl border ${r.ring} bg-card p-5`}>
                  <div className="flex items-center gap-2.5">
                    <HugeiconsIcon icon={r.icon} size={22} strokeWidth={1.8} className={r.accent} aria-hidden="true" />
                    <h4 className="font-display text-base font-bold text-foreground">{r.titulo}</h4>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{r.descripcion}</p>
                  <div className="mt-4 flex-1 border-t border-border/60 pt-4">
                    <p className="text-sm leading-relaxed text-foreground/80">{r.detalle}</p>
                  </div>
                  <span
                    className={`mt-4 inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${r.tagClass}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} aria-hidden="true" />
                    {r.tag}
                  </span>
                </div>
              </Anim>
            ))}
          </div>
        </section>

        {/* Ruta institucional */}
        <section aria-labelledby="ruta-institucional">
          <SectionTitle id="ruta-institucional">Ruta institucional</SectionTitle>
          <ol className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-6">
            {RUTA.map((paso, i) => (
              <Anim key={paso.n} {...enter(i * 0.06)}>
                <li className="relative flex flex-col items-center text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-sm font-bold text-white shadow-sm">
                    {paso.n}
                  </span>
                  <p className="mt-3 text-xs font-semibold leading-snug text-foreground">{paso.label}</p>
                  <HugeiconsIcon
                    icon={paso.icon}
                    size={26}
                    strokeWidth={1.5}
                    className="mt-3 text-primary/70"
                    aria-hidden="true"
                  />
                </li>
              </Anim>
            ))}
          </ol>
        </section>

        {/* Resguardos */}
        <section aria-label="Compromisos institucionales" className="rounded-2xl border border-border bg-muted/30 p-5">
          <p className="text-center text-sm text-foreground">
            <span className="font-bold text-primary">Siempre resguardamos:</span>{' '}
            <span className="text-muted-foreground">{RESGUARDOS.join(' · ')}</span>
          </p>
        </section>

        {/* Cierre */}
        <div className="flex items-center justify-center gap-2.5 text-center">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={22} className="shrink-0 text-primary" aria-hidden="true" />
          <p className="font-display text-base font-bold text-foreground">
            Por un uso de la IA con propósito, seguridad y responsabilidad.
          </p>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
      <h3 id={id} className="text-center text-sm font-bold uppercase tracking-wide text-primary">
        {children}
      </h3>
      <span className="h-px flex-1 bg-border" aria-hidden="true" />
    </div>
  );
}

function ControlCard({ numero, titulo, icon, accent, descripcion, nota }) {
  const isPrimary = accent === 'primary';
  const badge = isPrimary ? 'bg-primary text-white' : 'bg-secondary text-white';
  const title = isPrimary ? 'text-primary' : 'text-secondary';
  const noteBox = isPrimary ? 'bg-primary/[0.06] text-primary' : 'bg-secondary/[0.06] text-secondary';
  return (
    <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${badge}`}>
          {numero}
        </span>
        <h4 className={`font-display text-xl font-extrabold ${title}`}>{titulo}</h4>
        <HugeiconsIcon
          icon={icon}
          size={24}
          strokeWidth={1.5}
          className="ml-auto text-muted-foreground"
          aria-hidden="true"
        />
      </div>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{descripcion}</p>
      <div className={`mt-4 flex items-start gap-2.5 rounded-xl p-3.5 text-sm font-semibold ${noteBox}`}>
        <HugeiconsIcon icon={Shield01Icon} size={18} strokeWidth={1.8} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>{nota}</span>
      </div>
    </div>
  );
}
